import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/comics';

const aboutBgImage = 'https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20library%20or%20reading%20room%20with%20shelves%20of%20colorful%20comic%20books%20and%20manga%2C%20soft%20natural%20light%20streaming%20through%20windows%2C%20cozy%20atmosphere%2C%20wooden%20furniture%2C%20plants%2C%20clean%20modern%20interior%20design%2C%20peaceful%20reading%20environment&width=1600&height=600&seq=about-hero-01&orientation=landscape';

const faqItems = [
  {
    question: '皮皮喵漫画是否需要付费？',
    answer: '皮皮喵漫画目前所有内容均免费在线阅读，无需任何付费。我们致力于为广大漫画爱好者提供优质的阅读体验。',
  },
  {
    question: '漫画更新频率是怎样的？',
    answer: '我们会在原作者发布新章节后尽快同步更新。韩漫通常每周更新1-2话，日漫和国漫根据连载节奏同步更新，确保读者能够第一时间追更。',
  },
  {
    question: '支持哪些设备阅读？',
    answer: '皮皮喵漫画采用响应式设计，完美支持电脑端、平板和手机端访问。无论您使用什么设备，都能获得流畅舒适的阅读体验。',
  },
  {
    question: '如何找到我想看的漫画？',
    answer: '您可以通过首页的分类导航（韩漫、日漫、国漫、系列漫画）浏览，也可以使用搜索功能输入漫画名称进行查找。我们还会在首页推荐热门和最新更新的作品。',
  },
  {
    question: '遇到问题如何联系客服？',
    answer: '您可以通过邮件 contact@pipimao.com 或关注微信公众号"皮皮喵漫画"联系我们。客服热线：400-888-6666（工作日 9:00-18:00）。',
  },
];

const testimonials = [
  {
    name: '小猫咪爱漫画',
    content: '皮皮喵漫画真的太好用了！韩漫资源特别全，画质也很高清，再也不用去各种网站到处找了。',
    rating: 5,
  },
  {
    name: '二次元老司机',
    content: '追更功能太方便了，每次更新都能第一时间看到。推荐给了好多朋友，大家都说好用！',
    rating: 5,
  },
  {
    name: '追漫少女',
    content: '界面简洁好看，分类清晰，国漫资源也很多。希望能一直免费做下去，支持支持！',
    rating: 5,
  },
  {
    name: '深夜追更人',
    content: '最满意的就是更新速度，比其他网站快不少。热门漫画基本当天就能看到最新话。',
    rating: 5,
  },
  {
    name: '漫画推荐官',
    content: '推荐的漫画都很合我胃口，发现了好多宝藏作品。推荐算法很强，每次都能找到想看的！',
    rating: 4,
  },
  {
    name: '佛系看漫',
    content: '手机上也能流畅看，每话加载很快，阅读体验非常好。客服响应也很快，有问题马上解决。',
    rating: 5,
  },
];

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden pt-16">
          <img
            src={aboutBgImage}
            alt="关于皮皮喵漫画"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white font-heading drop-shadow-lg">
              关于我们
            </h1>
            <p className="mt-3 text-sm md:text-base text-white/80 max-w-lg drop-shadow-md">
              了解皮皮喵漫画的故事与使命
            </p>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="w-full bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                首页
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </nav>
          </div>
        </div>

        {/* Brand Story */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center">
              品牌故事
            </h2>
            <div className="mt-8 space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>
                <strong>皮皮喵漫画</strong>成立于2023年，是一群热爱漫画的年轻人共同打造的在线漫画阅读平台。我们的使命是让每一位漫画爱好者都能轻松找到、免费阅读自己喜欢的漫画作品。
              </p>
              <p>
                在创立之初，我们发现许多漫画爱好者面临着找漫画难、资源分散、阅读体验不佳等问题。于是我们决定创建一个汇集<strong>韩漫</strong>、<strong>日漫</strong>、<strong>国漫</strong>及<strong>系列漫画</strong>的综合平台，让用户在一个地方就能浏览所有类型的优质漫画。
              </p>
              <p>
                经过不断发展，皮皮喵漫画已经积累了数千部漫画资源，覆盖恋爱、热血、奇幻、修仙、搞笑、惊悚等多种题材。我们与国内外多家知名漫画平台合作，确保内容更新及时、画质高清。
              </p>
              <p>
                我们坚信漫画是连接人心的桥梁。无论你是喜欢浪漫韩漫的少女心读者，还是热衷于热血日漫的冒险爱好者，抑或是钟情于仙侠国漫的传统文化爱好者，皮皮喵漫画都为你准备了最精彩的内容。
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
              <div className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
                <span className="w-12 h-12 flex items-center justify-center mx-auto text-3xl">
                  <i className="ri-book-open-fill text-primary-500"></i>
                </span>
                <h3 className="mt-3 text-lg font-bold text-foreground-900 font-heading">海量资源</h3>
                <p className="mt-2 text-sm text-foreground-600">覆盖韩漫、日漫、国漫、系列漫画四大品类，持续更新扩充</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
                <span className="w-12 h-12 flex items-center justify-center mx-auto text-3xl">
                  <i className="ri-flashlight-fill text-primary-500"></i>
                </span>
                <h3 className="mt-3 text-lg font-bold text-foreground-900 font-heading">极速更新</h3>
                <p className="mt-2 text-sm text-foreground-600">与原作者同步更新，第一时间追更最新章节内容</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
                <span className="w-12 h-12 flex items-center justify-center mx-auto text-3xl">
                  <i className="ri-heart-fill text-primary-500"></i>
                </span>
                <h3 className="mt-3 text-lg font-bold text-foreground-900 font-heading">完全免费</h3>
                <p className="mt-2 text-sm text-foreground-600">所有漫画内容免费在线阅读，为漫友提供零门槛阅读体验</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center">
              用户口碑
            </h2>
            <p className="mt-2 text-sm text-foreground-600 text-center">
              听听漫友们怎么说
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {testimonials.map((t, i) => (
                <div
                  key={i}
                  className="p-4 md:p-5 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <span key={j} className="w-4 h-4 flex items-center justify-center">
                        <i
                          className={`text-sm ${
                            j < t.rating
                              ? 'ri-star-fill text-accent-500'
                              : 'ri-star-line text-foreground-300'
                          }`}
                        ></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    "{t.content}"
                  </p>
                  <p className="mt-3 text-xs text-foreground-500 font-medium">
                    — {t.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center">
              常见问题
            </h2>
            <p className="mt-2 text-sm text-foreground-600 text-center">
              关于皮皮喵漫画的常见疑问解答
            </p>
            <div className="mt-8 space-y-3">
              {faqItems.map((faq, i) => (
                <details
                  key={i}
                  className="group rounded-lg bg-background-100 border border-background-200/70 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 pr-4">
                      {faq.question}
                    </h3>
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-open:rotate-45">
                      <i className="ri-add-line text-foreground-500"></i>
                    </span>
                  </summary>
                  <div className="px-4 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center">
              联系我们
            </h2>
            <p className="mt-2 text-sm text-foreground-600 text-center">
              有任何问题或建议，欢迎随时联系我们
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-12 h-12 flex items-center justify-center mx-auto text-3xl">
                  <i className="ri-mail-line text-primary-500"></i>
                </span>
                <h3 className="mt-3 text-base font-bold text-foreground-900">电子邮件</h3>
                <p className="mt-2 text-sm text-foreground-600">contact@pipimao.com</p>
                <p className="text-xs text-foreground-500">工作日24小时内回复</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-12 h-12 flex items-center justify-center mx-auto text-3xl">
                  <i className="ri-wechat-line text-primary-500"></i>
                </span>
                <h3 className="mt-3 text-base font-bold text-foreground-900">微信公众号</h3>
                <p className="mt-2 text-sm text-foreground-600">皮皮喵漫画</p>
                <p className="text-xs text-foreground-500">关注获取最新更新通知</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-12 h-12 flex items-center justify-center mx-auto text-3xl">
                  <i className="ri-smartphone-line text-primary-500"></i>
                </span>
                <h3 className="mt-3 text-base font-bold text-foreground-900">客服热线</h3>
                <p className="mt-2 text-sm text-foreground-600">400-888-6666</p>
                <p className="text-xs text-foreground-500">工作日 9:00 - 18:00</p>
              </div>
            </div>

            {/* Quick Links */}
            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h3 className="text-base font-semibold text-foreground-900 text-center mb-4">
                探索更多漫画分类
              </h3>
              <div className="flex flex-wrap justify-center gap-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="px-4 py-2 rounded-full bg-background-50 text-foreground-700 text-sm border border-background-200/70 hover:border-primary-300 hover:text-primary-500 transition-all whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Schema.org Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "关于我们 - 皮皮喵漫画",
            "description": "了解皮皮喵漫画的品牌故事、用户口碑和联系方式。皮皮喵漫画汇集海量韩漫、日漫、国漫与热门系列漫画大全。",
            "url": "https://pipimao.com/about",
            "isPartOf": {
              "@type": "WebSite",
              "name": "皮皮喵漫画",
              "url": "https://pipimao.com"
            },
            "about": {
              "@type": "Organization",
              "name": "皮皮喵漫画",
              "url": "https://pipimao.com",
              "description": "皮皮喵漫画汇集海量韩漫、日漫、国漫与热门系列漫画大全，高清在线免费看，分类齐全。",
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "400-888-6666",
                "contactType": "customer service",
                "email": "contact@pipimao.com",
                "availableLanguage": "Chinese"
              }
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://pipimao.com" },
                { "@type": "ListItem", "position": 2, "name": "关于我们", "item": "https://pipimao.com/about" }
              ]
            },
            "hasPart": {
              "@type": "FAQPage",
              "mainEntity": faqItems.map((faq) => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            }
          }),
        }}
      />
    </>
  );
}